/**
 * WebCodecs Encoder Wrappers
 *
 * Provides VideoEncoder and AudioEncoder wrappers for encoding
 * rendered frames and mixed audio into compressed chunks.
 */

/**
 * Check if WebCodecs encoding is supported
 */
export function isEncodingSupported() {
  if (typeof window === "undefined") return false;
  return (
    typeof VideoEncoder !== "undefined" &&
    typeof AudioEncoder !== "undefined" &&
    typeof VideoFrame !== "undefined"
  );
}

/**
 * Video Encoder wrapper
 */
export class VideoEncoderWrapper {
  constructor(options = {}) {
    this.width = options.width || 1920;
    this.height = options.height || 1080;
    this.fps = options.fps || 30;
    this.bitrate = options.bitrate || 5_000_000; // 5 Mbps default
    this.codec = options.codec || "avc1.640028"; // H.264 High Profile Level 4.0

    this.encoder = null;
    this.isConfigured = false;
    this.encodedChunks = [];
    this.config = null;
    this.pendingFrames = 0;
    this.onChunk = options.onChunk || null;
    this.onError = options.onError || console.error;
  }

  async init() {
    // Check codec support
    const support = await VideoEncoder.isConfigSupported({
      codec: this.codec,
      width: this.width,
      height: this.height,
      bitrate: this.bitrate,
      framerate: this.fps,
    });

    if (!support.supported) {
      // Try fallback codecs
      const fallbackCodecs = [
        "avc1.42001E", // H.264 Baseline
        "vp8",
        "vp09.00.10.08", // VP9 Profile 0
      ];

      for (const fallback of fallbackCodecs) {
        const fallbackSupport = await VideoEncoder.isConfigSupported({
          codec: fallback,
          width: this.width,
          height: this.height,
          bitrate: this.bitrate,
          framerate: this.fps,
        });

        if (fallbackSupport.supported) {
          this.codec = fallback;
          console.log(`Using fallback codec: ${fallback}`);
          break;
        }
      }
    }

    this.encoder = new VideoEncoder({
      output: (chunk, meta) => this.handleOutput(chunk, meta),
      error: (e) => this.onError(e),
    });

    const config = {
      codec: this.codec,
      width: this.width,
      height: this.height,
      bitrate: this.bitrate,
      framerate: this.fps,
      // Request keyframes periodically
      latencyMode: "quality",
      // For H.264, use 'avc' format which mp4-muxer expects (not annexb)
      ...(this.codec.startsWith("avc1") ? { avc: { format: "avc" } } : {}),
    };

    this.encoder.configure(config);
    this.isConfigured = true;

    return this;
  }

  handleOutput(chunk, meta) {
    this.pendingFrames--;

    // Store config from first keyframe
    if (meta?.decoderConfig && !this.config) {
      this.config = meta.decoderConfig;
    }

    if (this.onChunk) {
      this.onChunk(chunk, meta);
    } else {
      this.encodedChunks.push({ chunk, meta });
    }
  }

  /**
   * Encode a video frame
   * @param {VideoFrame} frame - The video frame to encode
   * @param {Object} options - Encoding options
   */
  encode(frame, options = {}) {
    if (!this.isConfigured) {
      throw new Error("Encoder not configured");
    }

    this.pendingFrames++;
    this.encoder.encode(frame, {
      keyFrame: options.keyFrame || false,
    });
  }

  /**
   * Flush pending frames
   */
  async flush() {
    if (!this.encoder) return;
    await this.encoder.flush();
  }

  /**
   * Close the encoder
   */
  close() {
    if (this.encoder) {
      this.encoder.close();
      this.encoder = null;
    }
  }

  /**
   * Get encoder configuration for muxer
   */
  getConfig() {
    return this.config;
  }

  /**
   * Get all encoded chunks (if not using onChunk callback)
   */
  getChunks() {
    return this.encodedChunks;
  }
}

/**
 * Audio Encoder wrapper
 */
export class AudioEncoderWrapper {
  constructor(options = {}) {
    this.sampleRate = options.sampleRate || 48000;
    this.channels = options.channels || 2;
    this.bitrate = options.bitrate || 128_000; // 128 kbps default
    this.codec = options.codec || "mp4a.40.2"; // AAC-LC

    this.encoder = null;
    this.isConfigured = false;
    this.encodedChunks = [];
    this.config = null;
    this.pendingFrames = 0;
    this.onChunk = options.onChunk || null;
    this.onError = options.onError || console.error;
  }

  async init() {
    // Check codec support
    const support = await AudioEncoder.isConfigSupported({
      codec: this.codec,
      sampleRate: this.sampleRate,
      numberOfChannels: this.channels,
      bitrate: this.bitrate,
    });

    if (!support.supported) {
      // Try fallback codecs
      const fallbackCodecs = ["opus", "mp4a.40.2"];

      for (const fallback of fallbackCodecs) {
        const fallbackSupport = await AudioEncoder.isConfigSupported({
          codec: fallback,
          sampleRate: this.sampleRate,
          numberOfChannels: this.channels,
          bitrate: this.bitrate,
        });

        if (fallbackSupport.supported) {
          this.codec = fallback;
          console.log(`Using fallback audio codec: ${fallback}`);
          break;
        }
      }
    }

    this.encoder = new AudioEncoder({
      output: (chunk, meta) => this.handleOutput(chunk, meta),
      error: (e) => this.onError(e),
    });

    const config = {
      codec: this.codec,
      sampleRate: this.sampleRate,
      numberOfChannels: this.channels,
      bitrate: this.bitrate,
    };

    this.encoder.configure(config);
    this.isConfigured = true;

    return this;
  }

  handleOutput(chunk, meta) {
    this.pendingFrames--;

    // Store config from first chunk
    if (meta?.decoderConfig && !this.config) {
      this.config = meta.decoderConfig;
    }

    if (this.onChunk) {
      this.onChunk(chunk, meta);
    } else {
      this.encodedChunks.push({ chunk, meta });
    }
  }

  /**
   * Encode audio data
   * @param {AudioData} audioData - The audio data to encode
   */
  encode(audioData) {
    if (!this.isConfigured) {
      throw new Error("Encoder not configured");
    }

    this.pendingFrames++;
    this.encoder.encode(audioData);
  }

  /**
   * Flush pending audio
   */
  async flush() {
    if (!this.encoder) return;
    await this.encoder.flush();
  }

  /**
   * Close the encoder
   */
  close() {
    if (this.encoder) {
      this.encoder.close();
      this.encoder = null;
    }
  }

  /**
   * Get encoder configuration for muxer
   */
  getConfig() {
    return this.config;
  }

  /**
   * Get all encoded chunks (if not using onChunk callback)
   */
  getChunks() {
    return this.encodedChunks;
  }
}

/**
 * Create AudioData from Float32Array PCM samples
 * @param {Float32Array[]} channelData - Array of channel data
 * @param {number} sampleRate - Sample rate
 * @param {number} timestamp - Timestamp in microseconds
 */
export function createAudioData(channelData, sampleRate, timestamp) {
  const numberOfChannels = channelData.length;
  const numberOfFrames = channelData[0].length;

  // Interleave channels for AudioData
  const interleavedData = new Float32Array(numberOfFrames * numberOfChannels);
  for (let i = 0; i < numberOfFrames; i++) {
    for (let ch = 0; ch < numberOfChannels; ch++) {
      interleavedData[i * numberOfChannels + ch] = channelData[ch][i];
    }
  }

  return new AudioData({
    format: "f32-planar",
    sampleRate,
    numberOfChannels,
    numberOfFrames,
    timestamp,
    data: interleavedData,
  });
}
